// frontend/src/lib/flagged-fields.ts
// What TRF asked a family about, as the family reads it (#2520, #2527).
//
// Staff send an application or a renewal back with `flagged_fields` —
// `<section_id>.<field_id>` keys (services/formImpact.js) — and optionally
// `flagged_documents`. Every family surface that names those asks (the status
// page, /bills, Home, the wizard) reads the labels from here, so they cannot
// drift onto different wordings or fall back to raw keys differently.
//
// The labels come from the published application definition, localized by
// R2's `localizeVersion`. Renewals walk the same definition
// (ApplicationWizard loads `publishedApplication` for both flows), so one
// index serves both.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import type { FamilyApplication } from '@/types/applications';
import { localizeVersion } from '@/lib/formI18n';
import type { FormDefinitionVersion } from '@/lib/formDefinitions';
import { DOCUMENT_KIND_LABEL_KEY } from '@/lib/applications-display';
import { documentKindLabel } from '@/lib/documents';
import type { Msg } from '@/i18n/msg';
import { resolveFieldLabel, type FieldSpec } from '@/lib/intakeFields';

/**
 * Flag → the question's label. `short` (when present) is the field's own short
 * name — today the form builder's `committee_label`, which is English only, so
 * `askedFieldName` uses it only for an English reader (#2631).
 */
export type FlagLabeler = ((flag: string) => string | null) & { short?: (flag: string) => string | null };

/** True while the record is waiting on the family for named items. */
export function hasMoreInfoAsk(app: Pick<FamilyApplication, 'status' | 'flagged_fields' | 'flagged_documents'> | null | undefined): boolean {
  if (!app || app.status !== 'needs_more_info') return false;
  return (app.flagged_fields?.length ?? 0) > 0 || (app.flagged_documents?.length ?? 0) > 0;
}

/** The application's answers, keyed by section id — what a question worded
 *  off another section (`conditionalLabel.onSection`) reads its driver from. */
export type FlagAnswers = Partial<Record<string, Record<string, unknown> | undefined>>;

/**
 * Flag → question label, from a (localized) version's live fields. A flag
 * also resolves by its bare field id, for a flag written without a section.
 *
 * #2523 follow-up: a field worded for the family's country
 * (`conditionalLabel`, e.g. the distance question in kilometres for Canada)
 * is named the way the form words it for THIS family — `answers` are the
 * record's sections, read through the same resolver the form uses. Without
 * answers every field keeps its base label.
 */
export function flagLabelIndex(
  fields: ReadonlyArray<{ section_id: string; id: string; label: string; conditionalLabel?: FieldSpec['conditionalLabel']; committee_label?: string }>,
  answers?: FlagAnswers,
): FlagLabeler {
  const byFlag = new Map<string, string>();
  const shortByFlag = new Map<string, string>();
  for (const f of fields) {
    const label = f.conditionalLabel
      ? resolveFieldLabel(
        { key: f.id, label: f.label, type: 'text', conditionalLabel: f.conditionalLabel },
        answers?.[f.section_id],
        answers ?? {},
      )
      : f.label;
    byFlag.set(`${f.section_id}.${f.id}`, label);
    if (!byFlag.has(f.id)) byFlag.set(f.id, label);
    const short = f.committee_label?.trim();
    if (short) {
      shortByFlag.set(`${f.section_id}.${f.id}`, short);
      if (!shortByFlag.has(f.id)) shortByFlag.set(f.id, short);
    }
  }
  const labeler: FlagLabeler = (flag: string) => byFlag.get(flag) ?? null;
  labeler.short = (flag: string) => shortByFlag.get(flag) ?? null;
  return labeler;
}

/** Sentence-ending marks a quoted question drops, so "…?»." never happens. */
const TERMINAL = /[\s\u00a0\u202f]*[?？!！.。:：;；]+$/u;

/** Opening and closing quotation marks per language; French pads with a narrow no-break space. */
const QUOTES: Record<string, [string, string]> = {
  en: ['“', '”'],
  es: ['«', '»'],
  pt: ['“', '”'],
  fr: ['«\u202f', '\u202f»'],
  zh: ['“', '”'],
};

/**
 * #2631: how a sentence names one asked question — "They asked about {{items}}."
 * A short name is used as it is. A whole question ("En moyenne, combien
 * dépensez-vous … ?") dropped mid-sentence read as a run-on with a capital in
 * the middle and ended "?.", so it is set off as a quotation in the reader's
 * marks, with its own closing mark removed — the sentence supplies the stop.
 * A label reads as a question when it ends in a sentence mark or runs past
 * six words.
 */
export function askedFieldName(label: string, short: string | null | undefined, locale = 'en'): string {
  const lang = (locale || 'en').toLowerCase().split('-')[0];
  if (short && lang === 'en') return short;
  const text = label.trim();
  const isQuestion = TERMINAL.test(text) || text.split(/\s+/).length > 6;
  if (!isQuestion) return text;
  const [open, close] = QUOTES[lang] ?? QUOTES.en;
  return `${open}${text.replace(TERMINAL, '')}${close}`;
}

/**
 * The flagged fields' questions in the reader's language (#2109 L4). Fetched
 * only when there is a flag to name; any failure leaves `undefined`, and each
 * surface keeps its own fallback (the status page humanizes the key; /bills
 * and Home say "TRF needs something" without naming it).
 *
 * A plain effect, not react-query: the status page renders outside a query
 * client in its copy-editor preview.
 */
export function useFlaggedFieldLabels(app: FamilyApplication | null | undefined): FlagLabeler | undefined {
  return useFlaggedFieldLabelState(app).labeler;
}

/**
 * #2544: the labeler plus whether it is still on its way. `pending` is true
 * only while a wanted version is loading; a failed or impossible read settles
 * it (the surface then falls back), so a caller can wait for the family's
 * words instead of flashing a storage path for a moment.
 */
export function useFlaggedFieldLabelState(
  app: FamilyApplication | null | undefined,
): { labeler: FlagLabeler | undefined; pending: boolean } {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const wanted = Boolean(app && app.status === 'needs_more_info' && (app.flagged_fields?.length ?? 0) > 0);
  const [version, setVersion] = useState<FormDefinitionVersion | null>(null);
  // The language whose read failed; a new language is a new read.
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const loaderMissing = typeof api.forms?.publishedApplication !== 'function';

  useEffect(() => {
    if (!wanted) return;
    const load = api.forms?.publishedApplication;
    if (typeof load !== 'function') return;
    let cancelled = false;
    // Through a resolved promise, so a loader that throws or returns nothing
    // (a partial test mock) is a failed read, not a crashed effect.
    Promise.resolve()
      .then(() => load({ lang }))
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res?.data?.fields)) setVersion(res.data);
        else setFailedFor(lang);
      })
      .catch(() => { if (!cancelled) setFailedFor(lang); /* keep each surface's fallback */ });
    return () => { cancelled = true; };
  }, [wanted, lang]);

  const labeler = useMemo(() => {
    if (!version) return undefined;
    return flagLabelIndex(localizeVersion(version, lang).version.fields, app as unknown as FlagAnswers | undefined);
  }, [version, lang, app]);

  return { labeler, pending: wanted && !version && !loaderMissing && failedFor !== lang };
}

/** A requested document's kind as a message (no raw enum on screen, R3-B22). */
export function documentKindMsg(kind: string): Msg | string {
  const key = DOCUMENT_KIND_LABEL_KEY[kind as keyof typeof DOCUMENT_KIND_LABEL_KEY];
  return key ? { key } : documentKindLabel(kind);
}

/**
 * Everything TRF asked about, as the names a family reads: each flagged
 * question's label, then each requested document's kind. Null when a
 * flagged question has no label yet (definition still loading or failed) —
 * the caller then says "TRF needs something" without a list rather than
 * showing a storage key.
 */
export function askedItemNames(
  app: Pick<FamilyApplication, 'flagged_fields' | 'flagged_documents'>,
  fieldLabel: FlagLabeler | undefined,
  resolve: (m: Msg) => string,
  locale = 'en',
): string[] | null {
  const fields = app.flagged_fields ?? [];
  const names: string[] = [];
  for (const flag of fields) {
    const label = fieldLabel?.(flag);
    if (!label) return null;
    const name = askedFieldName(label, fieldLabel?.short?.(flag), locale);
    if (!names.includes(name)) names.push(name);
  }
  for (const d of app.flagged_documents ?? []) {
    const m = documentKindMsg(d.kind);
    const name = typeof m === 'string' ? m : resolve(m);
    if (name && !names.includes(name)) names.push(name);
  }
  return names.length > 0 ? names : null;
}

/**
 * The first wizard step holding something TRF asked about, or -1. `stepKeys`
 * is each step's flag keys (`<section>.<field>` for a screen's questions;
 * `documents` for the upload step), in walk order.
 */
export const DOCUMENTS_STEP_KEY = 'documents';

export function firstFlaggedStep(
  stepKeys: ReadonlyArray<ReadonlyArray<string>>,
  app: Pick<FamilyApplication, 'flagged_fields' | 'flagged_documents'>,
): number {
  const wanted = new Set(app.flagged_fields ?? []);
  if ((app.flagged_documents ?? []).length > 0) wanted.add(DOCUMENTS_STEP_KEY);
  if (wanted.size === 0) return -1;
  return stepKeys.findIndex((keys) => keys.some((k) => wanted.has(k)));
}
